import React, { useContext } from "react";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { ChasablesContext } from "../../context/ChasablesContext";
import { ToastContext } from "../../context/ToastContext";

type Props = {
  handleMarkAsComplete?: () => void;
  handleSkipMenuItemClicked?: (numberOfDays: number) => void;
  isLoading: boolean;
  invoicesNumber: string[];
  groupedInvoices?: boolean;
};

const SkipButton: React.FC<Props> = ({
  handleMarkAsComplete,
  handleSkipMenuItemClicked,
  isLoading,
  invoicesNumber,
  groupedInvoices,
}) => {
  const { showToast } = useContext(ToastContext);
  const { skipDays } = useContext(ChasablesContext);
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [selectedDateForSkip, setSelectedDateForSkip] = React.useState<string>(
    `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`
  );

  const [anchorSkipMenuEl, setAnchorSkipMenuEl] = React.useState<null | HTMLElement>(null);
  const openSkipMenu = Boolean(anchorSkipMenuEl);

  const handleOpenSkipMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorSkipMenuEl(event.currentTarget);
  };

  const handleCloseSkipMenu = () => {
    setAnchorSkipMenuEl(null);
  };

  const handleSkipMenuItemClick = async (numberOfDays: number) => {
    if (!invoicesNumber || invoicesNumber.length === 0) return;

    setAnchorSkipMenuEl(null);
    try {
      for (const invoiceNumber of invoicesNumber) {
        await skipDays(invoiceNumber, numberOfDays);
      }
      showToast(
        `${invoicesNumber.length} invoice${
          invoicesNumber.length > 1 ? "s" : ""
        } have been skipped for ${numberOfDays} day${numberOfDays > 1 ? "s" : ""}`,
        "success"
      );
      handleSkipMenuItemClicked?.(numberOfDays);
    } catch {
      showToast("The skipping of the invoice failed, please try again", "error");
    }
  };

  if (groupedInvoices && handleMarkAsComplete) {
    return (
      <Button
        variant="outlined"
        fullWidth={true}
        endIcon={isLoading ? <CircularProgress size={20} /> : <DoneIcon />}
        onClick={handleMarkAsComplete}
        disabled={isLoading}
      >
        Complete
      </Button>
    );
  }

  return (
    <>
      <Button variant="outlined" fullWidth={true} endIcon={<SkipNextIcon />} onClick={handleOpenSkipMenu}>
        Skip
      </Button>
      <Menu
        id="skip-menu"
        anchorEl={anchorSkipMenuEl}
        open={openSkipMenu}
        onClose={handleCloseSkipMenu}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {!groupedInvoices && handleSkipMenuItemClicked && (
          <>
            <MenuItem onClick={() => handleSkipMenuItemClick(1)}>For 1 day</MenuItem>
            <MenuItem onClick={() => handleSkipMenuItemClick(5)}>For 5 days</MenuItem>
            <MenuItem onClick={() => handleSkipMenuItemClick(15)}>For 15 days</MenuItem>
            <MenuItem onClick={() => handleSkipMenuItemClick(30)}>For 1 month</MenuItem>
            <MenuItem onClick={() => setIsCalendarOpen(true)}>Until a specific date</MenuItem>
          </>
        )}
        {handleMarkAsComplete && (
          <MenuItem
            onClick={() => handleMarkAsComplete()}
            sx={{
              bgcolor: "#edf5ff",
              color: "#005af8",
              borderRadius: 1,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            Mark as complete
            <DoneIcon fontSize="small" sx={{ mr: 1, color: "#005af8" }} />
          </MenuItem>
        )}
      </Menu>
      <Dialog onClose={() => setIsCalendarOpen(false)} open={isCalendarOpen}>
        <DialogTitle>Select a date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="Date"
            value={selectedDateForSkip}
            sx={{ marginTop: 2 }}
            onChange={(e) => setSelectedDateForSkip(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCalendarOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              handleCloseSkipMenu();
              setIsCalendarOpen(false);
              if (selectedDateForSkip) {
                // TODO: TEST THIS
                const daysUntilSelectedDateForSkip = Math.ceil(
                  (new Date(selectedDateForSkip).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                );
                handleSkipMenuItemClick(daysUntilSelectedDateForSkip);
              }
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SkipButton;
